/**
 * This component is a basic implementation of an autocomplete input,
 * intended to provide a simple way to create searchable select fields.
 * It manages its own open/close state and filtering logic while exposing
 * hooks for lazy loading and validation.
 * The component receives as input the following props:
 * - `value`: the value of the input that will be used to filter the options
 * - `rawItemsList`: an array of options to be displayed in the dropdown
 * - `label`: the label to be displayed next to the input
 * - `labelSecondary`: an optional secondary label to be displayed next to the label
 * - `placeholder`: the placeholder text to be displayed in the input
 * - `disabled`: a boolean to enable or disable the input
 * - `optionMap`: an object with the following properties:
 *   - `label`: the property name of the options to be used as the label, you can also provide an array of labels based on
 *      the shape of the objects in the `rawItemsList`, this feature only works on porperties in the first level
 *   - `value`: the property name of the options to be used as the value
 *   - `id`: the property name of the options to be used as the id
 * - `lazy`: a boolean to enable the lazy loading of the options. If set to true,
 *      the component will only load the options and return the selected item or the string the user is typing.
 *      If set to false, the component will load all the options when the component is initialized
 *      and will take the control about filtering the list of options based on the user's input.
 * - `mode`: this prop is used to set the component behavior. It can be 'select with search' or 'input with search', the default value is 'input',
 * - `validations`: an object with the validation rules for the input. The object should have the following structure:
 *   - `min`: the minimum value to be accepted
 *   - `max`: the maximum value to be accepted
 *   - `required`: a boolean to set the input as required
 *   - `email`: a boolean to validate the input as an email
 *   - `pattern`: a regex pattern to be used to validate the input
 *   - `minlength`: the minimum length of the input to be accepted
 *   - `maxlength`: the maximum length of the input to be accepted
 * - `appareance`: it an optional prop to pass a string to set the appareance of the component. It can be 'simple' or 'normal'
 *    this last one is the default.
 * - `textTransform`: it an optional prop intended to transform the text of the label. It can be 'uppercase', 'lowercase',
 *    'capitalize' or 'none' this last one is the default.
 * The component emits the `selectedItem` event when an option is selected.
 * The component emits the `input` event when the input is modified.
 *
 * Example of usage
 *
 * <fina-autocomplete
 *              [(ngModel)]="formData.address" // or [ngModel] for template driven forms
 *              [formControl]="form.controls[address]" // for reactive forms
 *              [name]="'address'"
 *              [value]="customer.address"
 *              [label]="'Dirección'"
 *              [labelSecondary]="'(OPCIONAL)'"
 *              [placeholder]="'Dirección de habitación del cliente...'"
 *              [rawItemsList]="customersList"
 *              [optionMap]="{ label: ['address','name'], value: 'address' }"
 *              [alternativeStyle]="'simple'"
 *              [textTransform]="'capitalize'"
 *              [lazy]="true"
 *              [mode]="'select'"
 *              [validations]="{ pattern: { value: '^[+]?[0-9\\s]*$', message: 'El formato es inválido' } }"
 *              (selectedItem)="onSelectCustomer($event)"
 *              (input)="onInput($event.target.value, 'address')"
 * ></fina-autocomplete>
 */

import { ConnectedPosition } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, ValidatorFn, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';

type ComponentMode = 'input' | 'select';

type MultilineLabel = {};

type CompoundLabel = {
    key: string;
    fallback?: string;
    showFallback?: boolean;
};

type Label = string;
interface OptionMap {
    value?: string;
    label: Label | Array<Label | CompoundLabel>;
    id?: string;
}

interface Option {
    value?: string;
    label: string;
    id?: string;
}

type ValidationsNames = 'min' | 'max' | 'required' | 'email' | 'pattern' | 'minlength' | 'maxlength' | 'noSame';
type ValidationObject = {
    [K in ValidationsNames]?: {
        value?: any;
        message?: string;
    };
};

type TextTransforms = 'none' | 'capitalize' | 'uppercase' | 'lowercase';
type Appearance = 'normal' | 'simple';
@Component({
    selector: 'fina-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteComponent), // Ensure `forwardRef` is used
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => AutocompleteComponent), // Register for validation
            multi: true,
        },
    ],
})
export class AutocompleteComponent implements OnInit, OnChanges, ControlValueAccessor, Validator, OnDestroy {
    private static instanceCounter = 0;

    @Input() value!: string;
    @Input() name!: string;
    @Input() label: string = '';
    @Input() labelSecondary: string = '';
    @Input() placeholder: string = '';
    @Input() rawItemsList: any[] = [];
    @Input() disabled = false;
    @Input() optionMap: OptionMap;
    @Input() validations: ValidationObject = {};
    @Input() lazy: boolean = false;
    @Input() mode: ComponentMode = 'input';
    @Input() appearance: Appearance = 'normal';
    @Input() optionsTextTransform: TextTransforms = 'none';
    @Input() forceTouch: boolean = false;
    /**
     * QA: cuando es `true`, el panel de opciones se renderiza en un CDK Overlay
     * (portaleado al body) para que NO lo recorte el `overflow` de un modal. Default
     * `false` → panel in-flow como siempre, así los demás usos del componente NO cambian
     * (solo los modales de ingreso pasan el flag en `true`).
     */
    @Input() panelInOverlay = false;
    @Output() selectedItem = new EventEmitter<Option>();

    @ViewChild('autocomplete') autocomplete!: ElementRef;
    @ViewChild('inputElementRef') inputElementRef!: ElementRef;

    inputFormControl: FormControl;

    private mappedItemsList: Option[] = [];
    private indexedItemsList = new Map<string, any>();
    private inputSuscription: Subscription;
    private _value: string = this.value;
    private onChange = (value: any) => {};
    private onTouched = () => {};
    filteredList: Option[] = [];
    isOptionsPanelVisible = false;
    highlightedIndex: number = -1;
    readonly optionsListId = `fina-autocomplete-options-${AutocompleteComponent.instanceCounter++}`;

    /** Posiciones del panel en modo overlay (panelInOverlay): debajo del input, fallback arriba. */
    readonly overlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    ];

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnInit() {
        if (!this.optionMap) {
            throw new Error('optionMap prop is required');
        }

        this.readData();
        this.configureInput();
    }

    ngOnDestroy() {
        this.inputSuscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        // Handle data list changes
        if (changes['rawItemsList']) {
            this.readData();
            this.refreshFilteredList();
            // Don't reconfigure input on every rawItemsList change
            // The subscription is already set up in ngOnInit
        }

        // Only reconfigure input if validation or disabled state changes
        // These are less frequent and actually require the FormControl to be recreated
        if (changes['validations'] && !changes['validations'].isFirstChange()) {
            this.configureInput();
        }

        if (changes['disabled'] && !changes['disabled'].isFirstChange()) {
            if (this.inputFormControl) {
                if (this.disabled) {
                    this.inputFormControl.disable();
                } else {
                    this.inputFormControl.enable();
                }
            }
        }

        if (changes['forceTouch'] && changes['forceTouch'].currentValue && this.inputFormControl) {
            this.inputFormControl.markAsTouched();
            this.cdr.markForCheck();
        }
    }

    private configureInput() {
        const validators = this.buildValidators();
        const oldInputValue = this.inputFormControl?.value || '';

        // Ensure we unsubscribe from any existing subscription
        if (this.inputSuscription) {
            this.inputSuscription.unsubscribe();
        }

        this.inputFormControl = new FormControl({ value: oldInputValue, disabled: this.disabled }, [...validators]);

        this.inputSuscription = this.inputFormControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(value => {
            this._value = value;
            this.onChange(value);
            this.refreshFilteredList();
        });
    }

    handleInputFocus(): void {
        this.showInitialOptions();
        this.openOptionsPanel();
    }

    handleInputKeydown(event: KeyboardEvent): void {
        if (this.disabled) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.openOptionsPanel();
                this.moveHighlight(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.openOptionsPanel();
                this.moveHighlight(-1);
                break;
            case 'Enter':
                if (this.isOptionsPanelVisible && this.highlightedIndex >= 0) {
                    event.preventDefault();
                    const highlightedOption = this.filteredList[this.highlightedIndex];
                    if (highlightedOption) {
                        this.onOptionSelected(highlightedOption);
                    }
                }
                break;
            case 'Escape':
                if (this.isOptionsPanelVisible) {
                    event.preventDefault();
                    this.closeOptionsPanel();
                }
                break;
        }
    }

    handleArrowClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.disabled) {
            return;
        }

        if (this.isOptionsPanelVisible) {
            this.closeOptionsPanel(true);
        } else {
            this.showInitialOptions();
            this.openOptionsPanel(true);
        }
    }

    onOptionSelected(option: Option): void {
        if (!option) {
            return;
        }
        this.setSelectedItem(option);
        this.closeOptionsPanel();
    }

    private openOptionsPanel(focusInput: boolean = false): void {
        if (this.disabled) {
            return;
        }

        if (!this.isOptionsPanelVisible) {
            this.isOptionsPanelVisible = true;
            this.setInitialHighlight();
        }

        if (focusInput) {
            this.focusInput();
        }
    }

    private closeOptionsPanel(enforceValidSelection: boolean = false): void {
        if (enforceValidSelection && this.mode === 'select') {
            this.ensureValidSelection();
        }

        this.isOptionsPanelVisible = false;
        this.highlightedIndex = -1;
    }

    private ensureValidSelection(): void {
        const currentValue = this.inputFormControl?.value;

        if (!currentValue) {
            this.clearNonValidSelectedOption(null);
            return;
        }

        const normalizedCurrentValue = currentValue.toString().toLowerCase().trim();
        const optionFound: Option | undefined = this.mappedItemsList.find(option => {
            if (option.value === undefined || option.value === null) {
                return false;
            }
            const optionValue = option.value.toString().toLowerCase().trim();
            return optionValue === normalizedCurrentValue;
        });

        if (!optionFound) {
            this.clearNonValidSelectedOption(null);
        }
    }

    private setInitialHighlight(): void {
        this.highlightedIndex = this.filteredList.length > 0 ? 0 : -1;
    }

    private ensureHighlightInRange(): void {
        if (this.filteredList.length === 0) {
            this.highlightedIndex = -1;
            return;
        }

        if (this.highlightedIndex < 0) {
            return;
        }

        if (this.highlightedIndex >= this.filteredList.length) {
            this.highlightedIndex = this.filteredList.length - 1;
        }
    }

    private moveHighlight(step: number): void {
        if (this.filteredList.length === 0) {
            this.highlightedIndex = -1;
            return;
        }

        const currentIndex = this.highlightedIndex === -1 ? (step > 0 ? -1 : 0) : this.highlightedIndex;
        const nextIndex = currentIndex + step;
        const normalizedIndex = (nextIndex + this.filteredList.length) % this.filteredList.length;
        this.highlightedIndex = normalizedIndex;
    }

    private buildValidators() {
        const validators: ValidatorFn[] = [];
        for (const key in this.validations) {
            switch (key) {
                case 'required':
                    validators.push(Validators.required);
                    break;
                case 'minlength':
                    validators.push(Validators.minLength(this.validations[key].value));
                    break;
                case 'maxlength':
                    validators.push(Validators.maxLength(this.validations[key].value));
                    break;
                case 'min':
                    validators.push(Validators.min(this.validations[key].value));
                    break;
                case 'max':
                    validators.push(Validators.max(this.validations[key].value));
                    break;
                case 'email':
                    validators.push(Validators.email);
                    break;
                case 'pattern':
                    validators.push(Validators.pattern(this.validations[key].value));
                    break;
                case 'noSame':
                    validators.push(this.noSameValidator(this.validations[key].value));
                    break;
            }
        }

        if (this.mode === 'select') {
            validators.push(this.optionValidator());
        }

        return validators;
    }

    // Agregar este método privado para el validador noSame
    private noSameValidator(forbiddenValue: any): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (!value) return null;

            // Comparar el valor actual con el valor prohibido
            const isSame = value.toString().toLowerCase().trim() === forbiddenValue.toString().toLowerCase().trim();

            return isSame ? { noSame: { value: forbiddenValue } } : null;
        };
    }

    private readData() {
        this.indexedItemsList = new Map<string, any>();

        const mappedOptions: Option[] = [];

        this.rawItemsList.forEach((item: any) => {
            const option = this.buildOptionFromItem(item);
            if (!option) {
                return;
            }

            mappedOptions.push(option);
            const optionKey = this.getOptionKey(option);
            if (optionKey) {
                this.indexedItemsList.set(optionKey, item);
            }
        });

        this.mappedItemsList = mappedOptions;
        this.refreshFilteredList();
    }

    private buildLabel(item: any): string {
        if (!Array.isArray(this.optionMap.label)) {
            return item[this.optionMap.label];
        }

        let label = '';

        this.optionMap.label.forEach((objectLabel: CompoundLabel | Label, index: number) => {
            const key = typeof objectLabel === 'string' ? objectLabel : objectLabel.key;

            if (!(key in item)) throw new Error(`The key ${key} doesn't exist in ${JSON.stringify(item)} of the rawItemsList.`);

            const nextLabel = item[key];

            const fallback = typeof objectLabel !== 'string' && typeof objectLabel.fallback === 'string' ? `( ${objectLabel.fallback} )` : '';

            const alternativeLabel = typeof objectLabel === 'string' ? `( no ${key} )` : fallback;

            label += ` ${nextLabel && index > 0 ? ' - ' : ''}${nextLabel ? nextLabel : alternativeLabel}`;
        });
        return label.trim();
    }

    private buildOptionFromItem(item: any): Option | null {
        if (item === null || item === undefined) {
            return null;
        }

        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
            const label = item.toString();
            return { label, value: label, id: label };
        }

        if (typeof item === 'object') {
            const label = this.buildLabel(item);
            const rawValueKey = this.optionMap?.value;
            const rawIdKey = this.optionMap?.id;
            const rawValue = rawValueKey ? item[rawValueKey] : label;
            const rawId = rawIdKey ? item[rawIdKey] : undefined;

            return {
                label,
                value: this.normalizeOptionValue(rawValue, label),
                id: rawId !== undefined && rawId !== null ? rawId.toString() : undefined,
            };
        }

        return null;
    }

    private normalizeOptionValue(rawValue: any, fallback: string): string {
        if (rawValue === undefined || rawValue === null) {
            return fallback;
        }

        return rawValue.toString();
    }

    private getOptionKey(option: Option): string {
        const source = option.id ?? option.value ?? option.label;
        return source !== undefined && source !== null ? source.toString() : '';
    }

    private clearNonValidSelectedOption(option: Option | null) {
        if (!option) {
            this.selectedItem.emit(null);
            this.clearInput();
            return;
        }
    }

    private setSelectedItem(option: Option) {
        this.inputFormControl.setValue(option.value);
        this.inputFormControl.markAsDirty();
        this._value = option.value;
        const optionKey = this.getOptionKey(option);
        const itemToReturn = this.indexedItemsList.get(optionKey);
        this.onChange(this._value);
        this.onTouched();
        this.selectedItem.emit(itemToReturn);
    }

    private clearInput() {
        if (this.inputFormControl) {
            this.inputFormControl.setValue('');
            this._value = '';
        }
    }

    private refreshFilteredList(): void {
        if (this.lazy) {
            this.filteredList = [...this.mappedItemsList];
        } else {
            const value = (this.inputFormControl?.value ?? '').toString().toLowerCase();
            this.filteredList = this.mappedItemsList.filter(item => item.label.toLowerCase().includes(value));
        }

        this.ensureHighlightInRange();
    }

    private showInitialOptions(): void {
        if (!this.inputFormControl) {
            return;
        }
        const currentValue = (this.inputFormControl.value ?? '').toString().trim();
        if (currentValue.length === 0) {
            this.filteredList = [...this.mappedItemsList];
            this.ensureHighlightInRange();
        }
    }

    private focusInput(): void {
        this.inputElementRef.nativeElement.focus();
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent): void {
        if (!this.isOptionsPanelVisible || !this.autocomplete) {
            return;
        }

        const clickedInside = this.autocomplete.nativeElement.contains(event.target);

        if (!clickedInside) {
            this.closeOptionsPanel(true);
        }
    }

    getErrorsMessages() {
        const errors = this.inputFormControl?.errors || null;

        if (!errors) {
            return null;
        }

        const errorsMapped = [];

        for (const error in errors) {
            let message = '';
            switch (error) {
                case 'required':
                    message = this.validations['required']?.message || 'Este campo es obligatorio';
                    break;
                case 'minlength':
                    message = this.validations['minlength']?.message || `El campo debe tener por lo menos ${errors.minlength.requiredLength} carácteres`;
                    break;
                case 'maxlength':
                    message = this.validations['maxlength']?.message || `El campo debe tener máximo ${errors.maxlength.requiredLength} carácteres`;
                    break;
                case 'min':
                    message = this.validations['min']?.message || `El valor mínimo permitido es ${errors.min.min}`;
                    break;
                case 'max':
                    message = this.validations['max']?.message || `El valor máximo permitido es ${errors.max.max}`;
                    break;
                case 'email':
                    message = this.validations['email']?.message || `El correo no tiene un formato válido`;
                    break;
                case 'pattern':
                    message = this.validations['pattern']?.message || `El texto no tiene un formato válido`;
                    break;
                case 'noSame':
                    message = this.validations['noSame']?.message || `El texto no puede ser ${this.validations['noSame'].value}`;
                    break;
                case 'noValidOption':
                    // message = 'La opción seleccionada no es válida';
                    break;
                default:
                    message = 'Error inesperado';
                    break;
            }
            const erroMapped = { error, message };
            errorsMapped.push(erroMapped);
        }

        return errorsMapped;
    }

    writeValue(value: string): void {
        if (value === null || value === undefined) return;

        this.inputFormControl?.setValue(value);
        this._value = value;
    }

    // Register the onChange function to notify Angular when the value changes
    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    // Register the onTouched function to notify Angular when the input is touched
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    // Validator Method
    validate(control: AbstractControl): ValidationErrors | null {
        return this.inputFormControl?.errors;
    }

    private optionValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (!value) return null;

            const normalizedValue = value.toString().toLowerCase().trim();
            const isValidOption = this.mappedItemsList.some(option => {
                if (option.value === undefined || option.value === null) {
                    return false;
                }
                return option.value.toString().toLowerCase().trim() === normalizedValue;
            });

            return isValidOption ? null : { noValidOption: true };
        };
    }
}
